import { writable } from 'svelte/store';

// Define the structure for action flags
interface ActionFlags {
  isUpdatingTeam: boolean;
  isCreatingTeam: boolean;
  isDeletingMember: boolean;
  isLoadingClubs: boolean;
  isDeletingAccount: boolean;
  isSavingTeam: boolean;
  isAddingTeamMember: boolean;
  isRemovingTeamMember: boolean;
  isLoadingPreferences: boolean;
  isUpdatingNotificationPreference: boolean;
  isUpdatingUserSettings: boolean;
  isLoadingTeamUsers: boolean;
  isInvitingUser: boolean;
  isRemovingUser: boolean;
  // Club management flags
  isLoadingAllClubs: boolean;
  isLoadingLogos: boolean;
  isCreatingClub: boolean;
  isUpdatingClub: boolean;
  isDeletingClub: boolean;
  isUploadingLogo: boolean;
  [key: string]: boolean; // Allow for dynamic flag additions
}

// Initial state - all flags are false
const initialFlags: ActionFlags = {
  isUpdatingTeam: false,
  isCreatingTeam: false,
  isDeletingMember: false,
  isLoadingClubs: false,
  isDeletingAccount: false,
  isSavingTeam: false,
  isAddingTeamMember: false,
  isRemovingTeamMember: false,
  isLoadingPreferences: false,
  isUpdatingNotificationPreference: false,
  isUpdatingUserSettings: false,
  isLoadingTeamUsers: false,
  isInvitingUser: false,
  isRemovingUser: false,
  // Club management flags
  isLoadingAllClubs: false,
  isLoadingLogos: false,
  isCreatingClub: false,
  isUpdatingClub: false,
  isDeletingClub: false,
  isUploadingLogo: false
};

// Create the writable store
export const actionFlags = writable<ActionFlags>(initialFlags);

// Helper functions to manage individual flags
export const actionFlagHelpers = {
  // Set a specific flag to true
  setFlag: (flagName: keyof ActionFlags) => {
    actionFlags.update(flags => ({
      ...flags,
      [flagName]: true
    }));
  },

  // Set a specific flag to false
  clearFlag: (flagName: keyof ActionFlags) => {
    actionFlags.update(flags => ({
      ...flags,
      [flagName]: false
    }));
  },

  clearFlags: (flagNames: (keyof ActionFlags)[]) => {
    for (const flagName of flagNames) {
      actionFlags.update(flags => ({
        ...flags,
          [flagName]: false
        }));
    }
  },

  // Toggle a specific flag
  toggleFlag: (flagName: keyof ActionFlags) => {
    actionFlags.update(flags => ({
      ...flags,
      [flagName]: !flags[flagName]
    }));
  },

  // Clear all flags (reset to initial state)
  clearAllFlags: () => {
    actionFlags.set(initialFlags);
  },

  // Check if any flag is currently active
  hasActiveFlags: (flags: ActionFlags): boolean => {
    return Object.values(flags).some(flag => flag === true);
  }
};
